import React from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button, CircularProgress } from '@material-ui/core';
import LockOpenIcon from '@material-ui/icons/LockOpen';
import { useTranslation } from 'react-i18next';
import { Title, PasswordTextField } from '../../elements';
import { PasswordPolicyChecklist } from '../../components';
import {
  notifications,
  extractApiErrorMessage,
  extractApiErrorCode,
  validatePasswordPolicy,
  AuthErrorCode
} from '../../utils';
import { ApiService } from '../../hooks';
import useFeatures, { FeatureType } from '../../hooks/features';
import { HealthSystemAPIControllersAuthResponsesInvitationDetailsResponse } from '../../api/HealthSystemApi';
import { brand } from '../../theme/brand';

const AcceptInvitation: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const { hasFeature } = useFeatures();
  const passwordPolicyEnabled = hasFeature(FeatureType.USE_PASSWORD_POLICY);

  const apiService = new ApiService();

  const [newPassword, setNewPassword] = React.useState('');
  const [confirmPassword, setConfirmPassword] = React.useState('');
  const [confirmError, setConfirmError] = React.useState(false);
  const [isBusy, setIsBusy] = React.useState(false);
  const [tokenInvalid, setTokenInvalid] = React.useState(false);
  const [invitationDetails, setInvitationDetails] =
    React.useState<HealthSystemAPIControllersAuthResponsesInvitationDetailsResponse | null>(null);

  // Fetch who/where the invite is for up front, so an invalid or expired invitation is caught
  // immediately instead of only after the user fills in a password and submits.
  React.useEffect(() => {
    if (!token) return;

    apiService.api.auth
      .authGetInvitationDetails({ token }, { format: 'json' })
      .then((res) => setInvitationDetails(res.data))
      .catch((e: unknown) => {
        if (extractApiErrorCode(e) === AuthErrorCode.InvitationTokenInvalid) {
          setTokenInvalid(true);
        }
      });
  }, [token]);

  const policy = validatePasswordPolicy(newPassword);
  const passwordsMatch = newPassword.length > 0 && newPassword === confirmPassword;
  const canSubmit = (!passwordPolicyEnabled || policy.isValid) && passwordsMatch && !isBusy;

  const handleSubmit = async () => {
    if (passwordPolicyEnabled && !policy.isValid) {
      notifications.warning(t('f-accept-invitation-policy-warning'));
      return;
    }

    if (!passwordsMatch) {
      setConfirmError(true);
      notifications.warning(t('f-accept-invitation-mismatch'));
      return;
    }

    setConfirmError(false);

    if (!token) {
      setTokenInvalid(true);
      return;
    }

    setIsBusy(true);

    await apiService.api.auth
      .authAcceptInvitation({ token, newPassword }, { format: 'json' })
      .then(() => {
        notifications.success(t('f-accept-invitation-success'));
        navigate('/login');
      })
      .catch((e: unknown) => {
        const code = extractApiErrorCode(e);
        if (code === AuthErrorCode.InvitationTokenInvalid) {
          setTokenInvalid(true);
        } else {
          notifications.error(extractApiErrorMessage(e, { fallback: t('f-accept-invitation-error') }));
        }
      })
      .finally(() => setIsBusy(false));
  };

  const renderInvalidInvitation = () => (
    <div className="p-6">
      <p className="text-sm text-gray-700 mb-4">{t('f-accept-invitation-invalid-link')}</p>
      <Button variant="outlined" color="primary" fullWidth onClick={() => navigate('/login')}>
        {t('f-accept-invitation-back-to-login')}
      </Button>
    </div>
  );

  return (
    <div className="flex justify-center py-10 px-4">
      <div className="w-full max-w-md bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="flex items-center gap-3 px-6 py-4 border-b border-gray-200 bg-gray-50">
          <img src={brand.logoSrc} alt={brand.logoAlt} className="w-8 h-8 rounded-full" />
          <Title>{t('f-accept-invitation-title')}</Title>
        </div>

        {!token || tokenInvalid ? (
          renderInvalidInvitation()
        ) : (
          <div className="p-6">
            <p className="text-sm text-gray-500 mb-4">{t('f-accept-invitation-info')}</p>

            {invitationDetails && (
              <div className="mb-4 rounded-md border border-gray-200 bg-gray-50 px-4 py-3 text-sm">
                {invitationDetails.farmName && (
                  <div className="flex justify-between gap-4">
                    <span className="text-gray-500">{t('f-usr-invite-farm')}</span>
                    <span className="font-medium text-gray-800">{invitationDetails.farmName}</span>
                  </div>
                )}
                <div className="flex justify-between gap-4">
                  <span className="text-gray-500">{t('f-com-email')}</span>
                  <span className="font-medium text-gray-800">{invitationDetails.email}</span>
                </div>
              </div>
            )}

            <PasswordTextField
              variant="outlined"
              margin="dense"
              fullWidth
              autoFocus
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              id="acceptInvitationPassword"
              label={t('f-accept-invitation-password')}
              disabled={isBusy}
            />

            {passwordPolicyEnabled && <PasswordPolicyChecklist password={newPassword} />}

            <PasswordTextField
              variant="outlined"
              margin="dense"
              fullWidth
              value={confirmPassword}
              onChange={(e) => {
                setConfirmPassword(e.target.value);
                if (confirmError) setConfirmError(false);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleSubmit();
              }}
              id="acceptInvitationConfirmPassword"
              label={t('f-accept-invitation-confirm-password')}
              error={confirmError}
              disabled={isBusy}
            />

            <Button
              id="acceptInvitationSubmitBtn"
              startIcon={isBusy ? <CircularProgress size={16} /> : <LockOpenIcon />}
              variant="contained"
              color="primary"
              fullWidth
              disabled={!canSubmit}
              className="!mt-4"
              onClick={handleSubmit}
            >
              {t('f-accept-invitation-submit')}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default AcceptInvitation;
